// src/components/PlanDayDetailHeader.js
import { useEffect, useMemo, useRef, useState } from "react";
import "./PlanDayDetailHeader.css";

function isYmd(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));
}

export default function PlanDayDetailHeader({
  ymd,
  typeLabel = "Rest",
  doneSets = 0,
  totalSets = 0,

  // parent mode
  isEditing = false, // ✅ NEW

  // callbacks
  onEditPlan, // (0|1)
  onSavePlan, // async ()
  saveBusy = false, // ✅ NEW

  onMarkDayDone,
  markDayBusy = false,

  onReplanDay,
}) {
  const safeDone = Number.isFinite(Number(doneSets)) ? Number(doneSets) : 0;
  const safeTotal = Number.isFinite(Number(totalSets)) ? Number(totalSets) : 0;
  const pct = safeTotal > 0 ? Math.round((safeDone / safeTotal) * 100) : 0;
  const completion = `${pct}%`;

  const [isRescheduling, setIsRescheduling] = useState(false);
  const [toYmd, setToYmd] = useState(ymd || "");
  const [busy, setBusy] = useState(false);
  const [localErr, setLocalErr] = useState("");

  // ✅ compact mode (if you already had this working)
  const rootRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const el = rootRef.current;
        if (!el) {
          ticking = false;
          return;
        }
        const top = el.getBoundingClientRect().top;
        const nextCompact = top <= 80;
        setIsCompact((prev) => (prev === nextCompact ? prev : nextCompact));
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // keep picker synced when navigating between days
  useEffect(() => {
    setToYmd(ymd || "");
    setIsRescheduling(false);
    setBusy(false);
    setLocalErr("");
  }, [ymd]);

  // ✅ if user enters edit mode, close reschedule view
  useEffect(() => {
    if (isEditing) {
      setIsRescheduling(false);
      setLocalErr("");
      setBusy(false);
    }
  }, [isEditing]);

  const canSubmit = useMemo(() => {
    if (!isYmd(ymd) || !isYmd(toYmd)) return false;
    if (toYmd === ymd) return false;
    return true;
  }, [ymd, toYmd]);

  async function submitReplan() {
    setLocalErr("");
    if (!canSubmit) return;

    try {
      setBusy(true);
      await onReplanDay?.(ymd, toYmd);
    } catch (e) {
      setLocalErr(e?.message || "Failed to reschedule this day.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSave() {
    setLocalErr("");
    try {
      await onSavePlan?.();
      // parent can choose to exit edit mode after successful save
    } catch (e) {
      setLocalErr(e?.message || "Failed to save plan changes.");
    }
  }

  function handleCancel() {
    setLocalErr("");
    setIsRescheduling(false);
    onEditPlan?.(0);
  }

  return (
    <div
      ref={rootRef}
      className={[
        "plan-day-detail-header",
        isCompact ? "is-compact" : "",
        isEditing ? "is-editing" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* TOP AREA */}
      <div className="plan-day-detail-top">
        {/* Normal view */}
        <div className={`plan-day-detail-top-pane ${!isRescheduling ? "is-active" : ""}`}>
          <div className="plan-day-detail-bubbles">
            <div className="plan-day-detail-bubble">
              <div className="plan-day-detail-bubble-label">Today&apos;s focus:</div>
              <div className="plan-day-detail-bubble-value">{typeLabel}</div>
            </div>

            <div className="plan-day-detail-bubble">
              <div className="plan-day-detail-bubble-label">Today&apos;s plan completion:</div>
              <div className="plan-day-detail-bubble-value">{completion}</div>
            </div>
          </div>
        </div>

        {/* Reschedule view */}
        <div className={`plan-day-detail-top-pane ${isRescheduling ? "is-active" : ""}`}>
          <div className="plan-day-detail-bubbles plan-day-detail-bubbles--single">
            <div className="plan-day-detail-bubble">
              <div className="plan-day-detail-bubble-label">Reschedule this day to:</div>

              <div className="plan-day-detail-picker-row">
                <input
                  className="plan-day-detail-date"
                  type="date"
                  value={toYmd || ""}
                  onChange={(e) => setToYmd(e.target.value)}
                  disabled={busy}
                />
              </div>

              {localErr ? <div className="plan-day-detail-error">{localErr}</div> : null}
            </div>
          </div>
        </div>
      </div>

      {/* BUTTONS */}
      {isEditing ? (
        <div className="plan-day-detail-actions plan-day-detail-actions--edit">
          <button
            type="button"
            className="btn"
            onClick={handleCancel}
            disabled={saveBusy}
          >
            Cancel
          </button>

          <div className="plan-day-detail-actions-spacer" />

          <button
            type="button"
            className="btn primary"
            onClick={handleSave}
            disabled={saveBusy}
            title="Save plan changes"
          >
            {saveBusy ? "Saving…" : "Save"}
          </button>
        </div>
      ) : !isRescheduling ? (
        <div className="plan-day-detail-actions">
          <button
            type="button"
            className="btn"
            onClick={() => onEditPlan?.(1)}
          >
            Edit plan
          </button>

          <button
            type="button"
            className="btn"
            onClick={() => setIsRescheduling(true)}
          >
            Reschedule
          </button>

          <button
            type="button"
            className="btn primary"
            onClick={() => onMarkDayDone?.()}
            disabled={markDayBusy || safeTotal === 0}
            title="Mark day as done"
          >
            {markDayBusy ? "Marking…" : "Mark day as done"}
          </button>
        </div>
      ) : (
        <div className="plan-day-detail-actions">
          <button
            type="button"
            className="btn"
            onClick={() => {
              setLocalErr("");
              setIsRescheduling(false);
            }}
            disabled={busy}
          >
            Back
          </button>

          <div className="plan-day-detail-actions-spacer" />

          <button
            type="button"
            className="btn primary"
            onClick={submitReplan}
            disabled={!canSubmit || busy}
            title={!canSubmit ? "Pick a different date" : "Move this day"}
          >
            {busy ? "Rescheduling…" : "Reschedule"}
          </button>
        </div>
      )}
    </div>
  );
}